import { useState, useRef, useEffect } from 'react';

interface Location {
  id: string;
  name: string;
}

interface ActiveFiltersSummaryProps {
  selectedLocations: Location[];
  availableLocations: Location[];
  otherFilters: string[];
  onLocationAdd: (locationId: string) => void;
  onLocationRemove: (locationId: string) => void;
  onShowAllFilters: () => void;
}

export function ActiveFiltersSummary({
  selectedLocations,
  availableLocations,
  otherFilters,
  onLocationAdd,
  onLocationRemove,
  onShowAllFilters,
}: ActiveFiltersSummaryProps) {
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get locations that aren't already selected
  const unselectedLocations = availableLocations.filter(
    (loc) => !selectedLocations.some((selected) => selected.id === loc.id)
  );

  const hasFilters = selectedLocations.length > 0 || otherFilters.length > 0;

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowLocationDropdown(false);
      }
    }

    if (showLocationDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showLocationDropdown]);

  if (!hasFilters) {
    return null;
  }

  const handleLocationRemove = (e: React.MouseEvent, locationId: string) => {
    e.stopPropagation();
    onLocationRemove(locationId);
  };

  const handleLocationAdd = (locationId: string) => {
    onLocationAdd(locationId);
    setShowLocationDropdown(false);
  };

  const handleAddLocationClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowLocationDropdown(!showLocationDropdown);
  };

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <svg
            className="w-5 h-5 text-blue-600 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
            />
          </svg>
          <span className="text-sm font-medium text-blue-900">Viewing:</span>

          {/* Location Badges with Remove */}
          {selectedLocations.length > 0 && (
            <div className="flex items-center gap-1 flex-wrap">
              {selectedLocations.map((location) => (
                <span
                  key={location.id}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300"
                >
                  📍 {location.name}
                  <button
                    onClick={(e) => handleLocationRemove(e, location.id)}
                    className="ml-1 hover:text-blue-900 focus:outline-none"
                    aria-label={`Remove ${location.name}`}
                  >
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </span>
              ))}

              {/* Add Location Button */}
              {unselectedLocations.length > 0 && (
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={handleAddLocationClick}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-blue-200 text-blue-900 border border-blue-400 hover:bg-blue-300 transition-colors"
                  >
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Add Location
                  </button>

                  {/* Dropdown */}
                  {showLocationDropdown && (
                    <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-md shadow-lg border border-gray-200 z-50 max-h-60 overflow-y-auto">
                      {unselectedLocations.map((location) => (
                        <button
                          key={location.id}
                          onClick={() => handleLocationAdd(location.id)}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-900 transition-colors"
                        >
                          📍 {location.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Other Filters */}
          {otherFilters.map((filter, index) => (
            <span key={index} className="text-sm text-blue-800">
              • {filter}
            </span>
          ))}
        </div>

        <button
          onClick={onShowAllFilters}
          className="flex items-center gap-2 text-blue-600 text-sm font-medium ml-4 hover:text-blue-800 transition-colors"
        >
          <span>Edit All Filters</span>
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}
